import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'webhook-trigger',
  version: '1.0.0',
  properties: [
    {
      key: 'path',
      label: 'Webhook Path',
      type: 'string',
      required: false,
      placeholder: '/my-webhook',
      description: 'Custom webhook path (leave empty for auto-generated URL)',
      validation: [
        {
          type: 'pattern',
          pattern: '^/[a-zA-Z0-9-_/]*$',
          message: 'Path must start with / and contain only alphanumeric characters, hyphens, underscores, and slashes',
        },
      ],
    },
    {
      key: 'webhookUrl',
      label: 'Webhook URL',
      type: 'notice',
      default: '',
    },
    {
      key: 'method',
      label: 'HTTP Method',
      type: 'options',
      required: true,
      default: 'POST',
      options: [
        { value: 'GET', name: 'GET' },
        { value: 'POST', name: 'POST' },
        { value: 'PUT', name: 'PUT' },
        { value: 'PATCH', name: 'PATCH' },
        { value: 'DELETE', name: 'DELETE' },
      ],
    },
    {
      key: 'authentication',
      label: 'Authentication',
      type: 'options',
      default: 'none',
      options: [
        { value: 'none', name: 'None' },
        { value: 'basicAuth', name: 'Basic Auth' },
        { value: 'headerAuth', name: 'Header Auth' },
        { value: 'jwtAuth', name: 'JWT Auth' },
        { value: 'urlParams', name: 'URL Parameters' },
      ],
      description: 'How external services should authenticate when calling this webhook',
    },
    
    // Basic Auth Configuration
    {
      key: 'basicAuthUser',
      label: 'Username',
      type: 'string',
      required: false,
      placeholder: 'username',
      description: 'Username for Basic Authentication',
      displayOptions: {
        show: {
          authentication: ['basicAuth'],
        },
      },
    },
    {
      key: 'basicAuthPassword',
      label: 'Password',
      type: 'string',
      required: false,
      placeholder: 'password',
      typeOptions: {
        password: true,
      },
      description: 'Password for Basic Authentication',
      displayOptions: {
        show: {
          authentication: ['basicAuth'],
        },
      },
    },
    
    // Header Auth Configuration
    {
      key: 'headerAuthName',
      label: 'Header Name',
      type: 'string',
      required: false,
      default: 'X-API-Key',
      placeholder: 'X-API-Key',
      description: 'Name of the authentication header',
      displayOptions: {
        show: {
          authentication: ['headerAuth'],
        },
      },
    },
    {
      key: 'headerAuthValue',
      label: 'Header Value',
      type: 'string',
      required: false,
      placeholder: 'your-secret-key',
      typeOptions: {
        password: true,
      },
      description: 'Expected value of the authentication header',
      displayOptions: {
        show: {
          authentication: ['headerAuth'],
        },
      },
    },
    
    // JWT Auth Configuration
    {
      key: 'jwtSecret',
      label: 'JWT Secret',
      type: 'string',
      required: false,
      placeholder: 'your-jwt-secret',
      typeOptions: {
        password: true,
      },
      description: 'Secret key to validate JWT tokens',
      displayOptions: {
        show: {
          authentication: ['jwtAuth'],
        },
      },
    },
    {
      key: 'jwtHeaderName',
      label: 'JWT Header Name',
      type: 'string',
      required: false,
      default: 'Authorization',
      placeholder: 'Authorization',
      description: 'Name of the header containing the JWT token',
      displayOptions: {
        show: {
          authentication: ['jwtAuth'],
        },
      },
    },

    // URL Parameters Auth Configuration
    {
      key: 'urlParamsCollection',
      label: 'URL Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      displayOptions: {
        show: {
          authentication: ['urlParams'],
        },
      },
      default: {},
      placeholder: 'Add Parameter',
      description: 'Key-value pairs that must be present in the URL query string',
      fixedCollectionOptions: [
        {
          name: 'parameters',
          displayName: 'Parameter',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              placeholder: 'api_key',
              required: true,
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: '',
              placeholder: 'your-secret-value',
              typeOptions: {
                password: true,
              },
            },
          ],
        },
      ],
    },
    {
      key: 'responseMode',
      label: 'Response Mode',
      type: 'options',
      default: 'immediate',
      options: [
        { value: 'immediate', name: 'Immediate response' },
        { value: 'last-node', name: 'When last node finishes' },
        { value: 'respond-node', name: 'Use Respond to Trigger node' },
      ],
      description: 'How to respond to the webhook request',
    },
    {
      key: 'responseData',
      label: 'Response Data',
      type: 'json',
      placeholder: '{ "status": "received" }',
      description: 'JSON data to return in the immediate response',
      displayOptions: {
        show: {
          responseMode: ['immediate'],
        },
      },
    },
    {
      key: 'testInput',
      label: 'Test Input',
      type: 'json',
      required: false,
      placeholder: '{\n  "name": "John",\n  "email": "john@example.com"\n}',
      description:
        'Test data to simulate a webhook payload. Just paste your JSON - it will be treated as the request body.',
      default: '{}',
      section: 'properties',
    },
  ],
};

export default schema;
